import React, { FunctionComponent, useState } from 'react';
import { BaseFormModalProps } from './BaseFormModal';
import { ApiResponse, PortDetails } from 'constants/api';
import { ApiDataBinding, withApiData } from 'react-api-data';
import { useTranslation } from 'react-i18next';
import { compose } from 'recompose';
import { BaseModalTemplate, FormModalButtons } from './BaseModalTemplate';
import Alert from 'reactstrap/lib/Alert';
import { NetworkRequestStatus, PortProvisioningActions, SnmpStatus } from 'constants/literals';
import DropdownItem from 'reactstrap/lib/DropdownItem';

interface InProps {
    port: PortDetails;
    snmpStatus: SnmpStatus;
    onChangeStatusRequested?: () => void;
}

interface EnhanceProps {
    portProvisioningActionsRequest: ApiDataBinding<ApiResponse<{}>>;
}

type Props = EnhanceProps & InProps & BaseFormModalProps;

const enhance = compose<Props, InProps>(
    withApiData(
        {
            portProvisioningActionsRequest: 'putPortProvisioningAction',
        },
        (ownProps: InProps) => ({
            portProvisioningActionsRequest: {
                exchange: ownProps.port.exchange.toUpperCase(),
                portId: ownProps.port.id,
            },
        })
    )
);

const PortProvisioningActionsModal: FunctionComponent<Props> = ({
    portProvisioningActionsRequest,
    port,
    snmpStatus,
    onChangeStatusRequested
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [provisioningAction, setProvisioningAction] = useState('');

    const toggle = () => {
        setIsOpen(!isOpen);
    };

    const onSubmit = () => {
        portProvisioningActionsRequest
            .perform(
                {
                    exchange: port.exchange.toUpperCase(),
                    portId: port.id,
                },
                {
                    provisioning_action: provisioningAction,
                }
            )
            .then((response) => {
                if (response.request.networkStatus === NetworkRequestStatus.Success) {
                    if (onChangeStatusRequested) onChangeStatusRequested();
                    toggle();
                }
            });       
    };

    const setDropDownMenu = () => {
        switch (snmpStatus) {
            case SnmpStatus.Up:
            case SnmpStatus.Down:
                return (
                    <>
                        <DropdownItem
                            onClick={() => {
                                toggle();
                                setProvisioningAction(PortProvisioningActions.disable);
                            }}
                        >
                            {t('portProvisioningActionsModal.requestDisable')}
                        </DropdownItem>
                        <DropdownItem
                            onClick={() => {
                                toggle();
                                setProvisioningAction(PortProvisioningActions.bounce);
                            }}
                        >
                            {t('portProvisioningActionsModal.requestBounce')}
                        </DropdownItem>
                    </>
                );
            case SnmpStatus.Disabled:
                return (
                    <DropdownItem
                        onClick={() => {
                            toggle();
                            setProvisioningAction(PortProvisioningActions.enable);
                        }}
                    >
                        {t('portProvisioningActionsModal.requestEnable')}
                    </DropdownItem>
                );
            default:
                return;
        }
    };

    return (
        <>
            <BaseModalTemplate
                isOpen={isOpen}
                toggle={toggle}
                header={t('portProvisioningActionsModal.headerTitle', { provisioningAction })}
                body={
                    <Alert color="warning" className="mb-0">
                        <strong>{t('common.warning')}</strong>
                        <p>
                            {t('portProvisioningActionsModal.warningMessagePortAction', { provisioningAction })}
                        </p>
                    </Alert>
                }
                footer={
                    <FormModalButtons
                        loading={portProvisioningActionsRequest.request.networkStatus === NetworkRequestStatus.Loading}
                        toggle={toggle}
                        onSubmit={onSubmit}
                    />
                }
            />
            <>
                {setDropDownMenu()}
            </>
        </>
    );
};

export default enhance(PortProvisioningActionsModal);
